import type { JSX } from 'react';
import { useState } from 'react';

import { Button } from '@/lib/ui/button';

import { usePhotosDeps } from './context';
import { fill, PhotoImage } from './PhotoImage';
import { useMyPhotos, useSignedUrl } from './queries';
import type { Photo } from './schemas';
import { strings } from './strings';

/**
 * §6 client `/me/photos` — the client's own gallery.
 *
 * Rule 6 is enforced by the api: this screen asks for "mine" and gets what is
 * the caller's. There is no photograph id in the request and therefore no way
 * for the browser to ask for somebody else's.
 *
 * **The share button is absent, not disabled, without `photo_marketing`.**
 * A disabled button invites a client to wonder what it would do; its absence
 * says nothing, which is correct, because they have not agreed to anything
 * and there is nothing to explain. The one case that *does* get an
 * explanation is a device with no share sheet at all — that is a limitation
 * of the device rather than a decision about their data.
 */
export function ShareButton({ photo }: { photo: Photo }): JSX.Element | null {
  const { share } = usePhotosDeps();
  const signed = useSignedUrl(photo.id, true);
  const [failed, setFailed] = useState(false);

  if (!photo.shareable || photo.deleted_at !== null) {
    return null;
  }
  if (share === undefined) {
    return (
      <span className="text-xs text-muted-foreground" data-testid="share-unavailable">
        {strings.shareUnavailable}
      </span>
    );
  }
  // Two shapes rather than one with a guard inside the handler: a button
  // that is disabled *and* re-checks the URL on click has a branch nothing
  // can reach, and an unreachable branch is a lie about what was tested.
  const url = signed.data?.url;
  if (url === undefined) {
    return (
      <Button variant="ghost" data-testid="photo-share" disabled>
        {strings.share}
      </Button>
    );
  }
  return (
    <>
      <Button
        variant="ghost"
        data-testid="photo-share"
        onClick={() => {
          setFailed(false);
          void share(url, strings.shareTitle).catch(() => {
            setFailed(true);
          });
        }}
      >
        {strings.share}
      </Button>
      {failed ? (
        <span role="alert" data-testid="share-failed">
          {strings.shareUnavailable}
        </span>
      ) : null}
    </>
  );
}

export function MyPhotosPage(): JSX.Element {
  const photos = useMyPhotos();

  return (
    <section className="space-y-4 p-4" data-testid="my-photos">
      <h1 className="text-xl font-semibold">{strings.myTitle}</h1>

      {photos.isPending ? <p>{strings.loading}</p> : null}
      {photos.isError ? (
        <p role="alert" data-testid="my-photos-failed">
          {strings.failed}
        </p>
      ) : null}

      {photos.isSuccess && photos.data.length === 0 ? (
        <div data-testid="my-photos-empty">
          <p>{strings.myEmpty}</p>
          <p className="text-sm text-muted-foreground">{strings.myEmptyHint}</p>
        </div>
      ) : null}

      <ul
        className="grid grid-cols-2 gap-3 @xl:grid-cols-3 @3xl:grid-cols-4 @6xl:grid-cols-6"
        data-testid="my-photo-grid"
      >
        {(photos.data ?? []).map((photo) => (
          <li key={photo.id} className="space-y-1" data-testid="my-photo-cell">
            <div className="aspect-square overflow-hidden rounded">
              <PhotoImage photo={photo} own className="h-full w-full object-cover" />
            </div>
            <span className="block text-xs text-muted-foreground">
              {fill(strings.takenOn, { when: photo.taken_at.slice(0, 10) })}
            </span>
            {photo.deleted_at === null ? (
              <span className="block text-xs text-muted-foreground" data-testid="kept-until">
                {fill(strings.keptUntil, { when: photo.retain_until })}
              </span>
            ) : null}
            <ShareButton photo={photo} />
          </li>
        ))}
      </ul>
    </section>
  );
}
